import { Controller } from "@hotwired/stimulus"
import { logger } from "../logger"

// Connects to data-controller="sheet-list-sortable"
//
// Enables drag-and-drop reordering of report sheets in the properties sidepanel.
// Integrates with the report autosave system for persistence.
//
// Usage:
//   .sheet-list[
//     data-controller="sheet-list-sortable"
//     data-sheet-list-sortable-sheet-index-value="0"
//   ]
//     .sheet-item[data-sheet-id="123"]
//       .sheet-drag-handle[data-action="mousedown->sheet-list-sortable#handleMouseDown"]
//         i.fa.fa-grip-vertical
//       .sheet-info
//         .sheet-title Sheet 1
//         .sheet-data-set DataSet::GroupDataSet
//
export default class extends Controller {
  static values = {
    sheetIndex: Number  // Index for form field names (e.g., report[report_sheets_attributes][0])
  }

  connect() {
    // Check if we're inside an element that's being dragged
    const isDragging = this.element.closest('[data-sortable-dragging="true"]')
    if (isDragging) {
      this.shouldInitializeOnNextConnect = true
      return
    }

    if (this.shouldInitializeOnNextConnect) {
      this.shouldInitializeOnNextConnect = false
    }

    // Sortable is loaded globally via CDN in application.slim
    if (typeof Sortable === 'undefined') {
      logger.error('[Sheet List Sortable] ERROR: Sortable.js library not loaded')
      return
    }

    this.initializeSortable()
  }

  disconnect() {
    const isDragging = this.element.closest('[data-sortable-dragging="true"]')
    if (isDragging) {
      return
    }

    if (this.shouldInitializeOnNextConnect) {
      return
    }

    if (this.sortable) {
      this.sortable.destroy()
    }
  }

  initializeSortable() {
    this.sortable = Sortable.create(this.element, {
      animation: 150,
      handle: '.sheet-drag-handle',  // Only drag by the handle
      draggable: '.sheet-item',       // Items that can be dragged
      dragClass: 'sortable-drag',
      ghostClass: 'sortable-ghost',
      chosenClass: 'sortable-chosen',

      onStart: (evt) => {
        this.handleDragStart(evt)
      },

      onEnd: (evt) => {
        this.handleDragEnd(evt)
      }
    })
  }

  handleDragStart(evt) {
    logger.log('[Sheet List Sortable] Drag started:', {
      item: evt.item,
      oldIndex: evt.oldIndex
    })

    // Mark the dragged item
    evt.item.dataset.sortableDragging = 'true'
  }

  handleDragEnd(evt) {
    logger.log('[Sheet List Sortable] Drag ended:', {
      item: evt.item,
      from: evt.from,
      to: evt.to,
      oldIndex: evt.oldIndex,
      newIndex: evt.newIndex
    })

    // Remove the dragging marker
    delete evt.item.dataset.sortableDragging

    // Update position values in the form
    this.updatePositions()

    // Update the header tabs to reflect the new order
    this.updateHeaderTabs()

    // Trigger auto-save
    const autoSaveEvent = new CustomEvent('report-autosave:schedule', {
      bubbles: true,
      detail: { source: 'sheet-list-sortable' }
    })
    this.element.dispatchEvent(autoSaveEvent)

    logger.log('[Sheet List Sortable] Position update complete, autosave scheduled')
  }

  updatePositions() {
    // Find all sheet items in order
    const sheetItems = this.element.querySelectorAll('.sheet-item')

    sheetItems.forEach((item, index) => {
      // Find the position input field for this sheet
      const sheetId = item.dataset.sheetId
      if (!sheetId) {
        logger.warn('[Sheet List Sortable] Sheet item missing data-sheet-id:', item)
        return
      }

      // Find the hidden position input field in the main form
      // Pattern: report[report_sheets_attributes][SHEET_INDEX][position]
      // We need to find by the sheet ID field and then update its sibling position field
      const idInput = document.querySelector(`input[name*="[report_sheets_attributes]"][name*="[id]"][value="${sheetId}"]`)

      if (idInput) {
        // Extract the index from the id input's name
        // Example: report[report_sheets_attributes][0][id] -> 0
        const match = idInput.name.match(/\[report_sheets_attributes\]\[(\d+)\]\[id\]/)
        if (match) {
          const sheetIndex = match[1]
          const positionInput = document.querySelector(`input[name="report[report_sheets_attributes][${sheetIndex}][position]"]`)

          if (positionInput) {
            const oldPosition = positionInput.value
            positionInput.value = index
            logger.log(`[Sheet List Sortable] Updated sheet ${sheetId} position: ${oldPosition} -> ${index}`)
          } else {
            logger.warn(`[Sheet List Sortable] Position input not found for sheet ${sheetId} at index ${sheetIndex}`)
          }
        }
      } else {
        logger.warn(`[Sheet List Sortable] ID input not found for sheet ${sheetId}`)
      }
    })
  }

  updateHeaderTabs() {
    // Reorder the header tabs to match the new sheet order in the manage sheets panel
    const headerTabsContainer = document.querySelector('.header-tabs')
    if (!headerTabsContainer) {
      logger.warn('[Sheet List Sortable] Header tabs container not found')
      return
    }

    // Get the new order of sheet IDs from the manage sheets panel
    const sheetItems = this.element.querySelectorAll('.sheet-item')
    const orderedSheetIds = Array.from(sheetItems).map(item => item.dataset.sheetId)

    logger.log('[Sheet List Sortable] New sheet order:', orderedSheetIds)

    // Get all sheet tab buttons from the header
    const allTabs = Array.from(headerTabsContainer.querySelectorAll('.tab-button[data-sheet-id]'))

    // Create a map of sheet ID to tab button (using data-sheet-id attribute)
    const sheetIdToTab = new Map()
    allTabs.forEach(tab => {
      const sheetId = tab.dataset.sheetId
      if (sheetId) {
        sheetIdToTab.set(sheetId, tab)
        logger.log(`[Sheet List Sortable] Mapped tab to sheet ID: ${sheetId}`)
      }
    })

    // Reorder tabs in the container based on the new order
    // appendChild moves elements, so calling it in order will reorder them correctly
    orderedSheetIds.forEach((sheetId) => {
      const tab = sheetIdToTab.get(sheetId)
      if (tab) {
        headerTabsContainer.appendChild(tab)
      } else {
        logger.warn(`[Sheet List Sortable] Tab not found for sheet ID: ${sheetId}`)
      }
    })

    logger.log('[Sheet List Sortable] Header tabs reordered successfully')
  }
}
